<script setup lang="ts">
import HospitalTimelineService, {TimelineEntryConfig} from "@/hospital/services/HospitalTimelineService";
import {HospitalTimelineEntry} from "@/hospital/interfaces/hospital-timeline";

interface Props {
    entry: HospitalTimelineEntry
    config: TimelineEntryConfig
    chatBubble?: boolean
}

const props = defineProps<Props>();

const formatTimelineDate = (dateString: string): string => {
    return new Date(dateString).toLocaleString('fr-FR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
    });
};
</script>

<template>
  <div v-if="props.chatBubble" class="flex w-full" :class="HospitalTimelineService.isFromCurrentEnvironment(entry) ? 'justify-start' : 'justify-end'">
    <div class="w-full flex flex-col">
      <div
        class="relative px-4 py-3 rounded-2xl shadow-sm max-w-full break-words"
        :class="{
          'bg-blue-500 text-white': HospitalTimelineService.isFromCurrentEnvironment(entry),
          'bg-gray-200 text-gray-900': !HospitalTimelineService.isFromCurrentEnvironment(entry)
        }"
      >
        <div
          class="absolute top-3 w-0 h-0"
          :class="{
            '-right-2 border-l-[12px] border-r-gray-200 border-t-[6px] border-t-transparent border-b-[6px] border-b-transparent': !HospitalTimelineService.isFromCurrentEnvironment(entry),
            '-left-2 border-r-[12px] border-r-blue-500 border-t-[6px] border-t-transparent border-b-[6px] border-b-transparent': HospitalTimelineService.isFromCurrentEnvironment(entry)
          }"
        />

        <div>
          <h3 class="text-sm font-semibold mb-1" :class="HospitalTimelineService.isFromCurrentEnvironment(entry) ? 'text-white' : 'text-gray-900'">
            {{ entry.display_title }}
          </h3>
          <p class="text-sm whitespace-pre-wrap" :class="HospitalTimelineService.isFromCurrentEnvironment(entry) ? 'text-white' : 'text-gray-700'">
            {{ entry.display_description }}
          </p>
        </div>

        <slot />
      </div>

      <div class="flex items-center gap-2 mt-1 px-2" :class="HospitalTimelineService.isFromCurrentEnvironment(entry) ? 'flex-row-reverse' : 'flex-row'">
        <time class="text-xs text-gray-500">
          {{ formatTimelineDate(entry.created_at) }}
        </time>
        <slot name="user-info" />
      </div>
    </div>
  </div>

  <div
    v-else
    class="border-[1.5px] bg-white rounded-lg p-4 shadow-sm hover:shadow-md transition-shadow" :class="{
      'border-primary': HospitalTimelineService.isFromCurrentEnvironment(entry),
      'border-secondary': !HospitalTimelineService.isFromCurrentEnvironment(entry)
    }"
  >
    <div class="flex items-start justify-between">
      <div class="flex-1">
        <h3 class="text-sm font-semibold text-gray-900 mb-1">
          {{ entry.display_title }}
        </h3>
        <p class="text-sm text-gray-600 mb-2 break-words whitespace-pre-wrap">
          {{ entry.display_description }}
        </p>
      </div>
    </div>

    <slot />

    <div class="flex items-center justify-between mt-3 pt-3 border-t-[1px] border-gray-200 gap-2">
      <div class="flex flex-wrap gap-2">
        <time class="text-xs text-gray-500">
          {{ formatTimelineDate(entry.created_at) }}
        </time>
        <slot name="user-info" />
      </div>
    </div>
  </div>
</template>
